import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { CircularProgress, Grid, Stack, Typography } from '@mui/material'
import OnboardingHeader from '../../components/OnboardingHeader/OnboardingHeader'
import { Trans } from 'react-i18next'
import InputTextField from '../../../../components/InputTextField/InputTextField'
import TagManager from '@sooro-io/react-gtm-module'
import PasswordConfirmation from './PasswordConfirmation/PasswordConfirmation'
import FeatureCarousel from '../../../../components/FeatureCarousel/FeatureCarousel'
import {
	actionSaveOnboardingData,
	selectOnboardingData,
	selectStripeCustomerData,
} from '../../../../store/slices/user/userSlice'
import { useAppDispatch, useAppSelector } from '../../../../store/utils/hooks'
import { useTheme } from '@mui/material/styles'
import { Status } from '../../../../store/types'
import PaymentSetup from './PaymentSetup/PaymentSetup'
import { StripeElementsOptionsClientSecret } from '@stripe/stripe-js/dist/stripe-js/elements-group'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useActionFetchUser } from '../../../../store/hooks/user/useActionFetchUser'
import { useOnboardingProgress } from '../../hooks/useOnboardingProgress'
import OnboardingNavigation from '../../components/OnboardingNavigation/OnboardingNavigation'
import { ONBOARDING_PATH } from '../../../../routes/OnboardingRoutes'
import { useNavigate } from 'react-router-dom'
import useCommonVars from '../../../../utils/FeatureFlags/useCommonVars'

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '')

export default function AccountSection() {
	const { palette } = useTheme()
	const navigate = useNavigate()
	const dispatch = useAppDispatch()
	const onboardingData = useAppSelector(selectOnboardingData)
	const [validAccount, setValidAccount] = useState<boolean>(false)
	const [validPassword, setValidPassword] = useState<boolean>(false)
	const [hasTrackedFormInteraction, setHasTrackedFormInteraction] = useState<boolean>(
		sessionStorage.getItem('hasTrackedFormInteraction') === 'true',
	)
	const { status: checkoutSessionStatus, data: stripeCustomerData } = useAppSelector(selectStripeCustomerData)
	const { userData } = useActionFetchUser()
	const hasCompletedRegister = useOnboardingProgress('register', '')
	const {
		PROPAI_ONBOARDING_TRIAL_PERIOD,
		PROPAI_ONBOARDING_INITIAL_PLAN_COST,
		PROPAI_ONBOARDING_INITIAL_PLAN_CALLS,
	} = useCommonVars()

	const isLoadingStripe = checkoutSessionStatus === Status.PENDING || !stripeCustomerData?.client_secret

	const setFirstName = useCallback(
		(firstName: string) => dispatch(actionSaveOnboardingData({ firstName })),
		[dispatch],
	)
	const setLastName = useCallback((lastName: string) => dispatch(actionSaveOnboardingData({ lastName })), [dispatch])
	const setCompanyName = useCallback(
		(companyName: string) => dispatch(actionSaveOnboardingData({ companyName })),
		[dispatch],
	)

	const options = useMemo(
		() =>
			({
				clientSecret: stripeCustomerData?.client_secret || '',
				locale: 'en',
				appearance: {
					theme: 'stripe',
					variables: {
						borderRadius: '10px',
						colorPrimary: palette.primary.main,
						colorText: palette.primary.main,
						colorTextSecondary: palette.primary.main,
						colorBackground: 'white',
					},
				},
			}) as StripeElementsOptionsClientSecret,
		[palette.primary.main, stripeCustomerData?.client_secret],
	)

	useEffect(() => {
		const validFirstName = !!onboardingData?.firstName?.trim()
		const validLastName = !!onboardingData?.lastName?.trim()
		setValidAccount(validFirstName && validLastName && validPassword)
	}, [onboardingData.firstName, onboardingData.lastName, validPassword])

	const userExist = useMemo(() => !!Object.keys(userData).length, [userData])

	const navBack = useCallback(() => navigate(ONBOARDING_PATH), [navigate])

	const trackFormInteraction = useCallback(() => {
		if (!hasTrackedFormInteraction) {
			TagManager.dataLayer({
				dataLayer: {
					event: 'formInteraction',
					formInteraction: {
						property: 'webflow',
						form: 'initiateCheckout',
					},
				},
			})
			setHasTrackedFormInteraction(() => {
				sessionStorage.setItem('hasTrackedFormInteraction', String(true))
				return true
			})
		}
	}, [hasTrackedFormInteraction])

	if (!hasCompletedRegister) {
		return null
	}

	return (
		<Grid container maxWidth="100%" justifyContent="center">
			<Grid item xs={0} sm={2} md={2} />
			<Grid item xs={12} sm={4} md={4} lg={4}>
				<Stack gap="16px" marginBottom="32px">
					<img src="/assets/images/onboarding/payment.svg" alt="" width="52px" />
					<Typography fontSize="12px">
						<Trans i18nKey="onboarding.layout.step_1" components={{ b: <b /> }} />
					</Typography>
					<OnboardingHeader i18nHeader="onboarding.payment.title" />
					<Typography fontSize="14px" color={palette.text.darkGrey}>
						<Trans
							i18nKey="onboarding.payment.subtitle"
							components={{ b: <b style={{ color: palette.brand.main }} /> }}
							values={{
								trialPeriod: PROPAI_ONBOARDING_TRIAL_PERIOD,
								initialPlanCost: PROPAI_ONBOARDING_INITIAL_PLAN_COST,
								initialPlanCalls: PROPAI_ONBOARDING_INITIAL_PLAN_CALLS,
							}}
						/>
					</Typography>
					<FeatureCarousel />
					<Stack direction="row" gap="16px">
						<InputTextField
							required
							i18nLabel="onboarding.payment.first_name_label"
							name="first_name_label"
							onChange={(e) => setFirstName(e.target.value)}
							onFocus={() => trackFormInteraction()}
							value={onboardingData?.firstName}
							disabled={userExist}
							sx={{ flexGrow: 1 }}
						/>

						<InputTextField
							required
							i18nLabel="onboarding.payment.last_name_label"
							name="last_name_label"
							onChange={(e) => setLastName(e.target.value)}
							onFocus={() => trackFormInteraction()}
							value={onboardingData?.lastName}
							disabled={userExist}
							sx={{ flexGrow: 1 }}
						/>
					</Stack>
					<InputTextField
						i18nLabel="onboarding.payment.company_name_label"
						name="company_name_label"
						onChange={(e) => setCompanyName(e.target.value)}
						onFocus={() => trackFormInteraction()}
						value={onboardingData?.companyName}
						disabled={userExist}
					/>
					<PasswordConfirmation
						disabled={userExist}
						onPasswordChange={(v: boolean) => setValidPassword(v)}
						onFocus={() => trackFormInteraction()}
					/>
					{isLoadingStripe && (
						<Stack minHeight="200px" justifyContent="center" alignItems="center" gap="16px">
							<CircularProgress />
							<OnboardingNavigation
								i18nForwardLabel="onboarding.payment.subscribe_button_label"
								backClick={navBack}
								forwardClick={() => {}}
								disableForward={true}
							/>
						</Stack>
					)}
					{!isLoadingStripe && stripeCustomerData?.client_secret && (
						<Elements stripe={stripePromise} options={options}>
							<PaymentSetup options={options} validAccount={validAccount} />
						</Elements>
					)}
				</Stack>
			</Grid>
			<Grid item xs={0} sm={2} md={2} />
		</Grid>
	)
}
